/**
 * Report Generator
 * Creates human-readable, shareable reports from analytics data
 * Designed for non-technical team members
 */

import { formatCurrencyUSD, formatNumberCompact, formatPercent } from './format';
import { formatDate } from './formatPreferences';

/**
 * Generate a comprehensive summary report from dashboard data
 * @param {Object} data - Dashboard analytics data
 * @param {string} period - Time period
 * @returns {Object} - Formatted report object
 */
export const generateSummaryReport = (data, period = 'All Time') => {
  const {
    userData,
    splitData,
    txData,
    walletData,
    badgeData,
    phoneAuthData,
    financialFlow
  } = data;

  const report = {
    title: `WeSplit Analytics Report - ${period}`,
    generatedAt: new Date().toISOString(),
    period,
    summary: {
      overview: generateOverviewSection(userData, period),
      users: generateUserSection(userData),
      financials: generateFinancialSection(txData, financialFlow, splitData, walletData),
      activity: generateActivitySection(splitData, badgeData, phoneAuthData),
      insights: generateInsights(data)
    }
  };

  return report;
};

/**
 * Generate overview section
 */
const generateOverviewSection = (userData, period) => {
  return {
    title: '📊 Overview',
    description: `Key metrics for ${period}`,
    metrics: [
      {
        label: 'Total Users',
        value: formatNumberCompact(userData?.totalUserCount || 0),
        description: 'All registered users',
        trend: userData?.growth?.totalUserCount ? `${formatPercent(userData.growth.totalUserCount, 1)} vs previous period` : null
      },
      {
        label: 'Active Users',
        value: formatNumberCompact(userData?.activeUsers || 0),
        description: 'Users active in this period',
        trend: userData?.growth?.activeUsers ? `${formatPercent(userData.growth.activeUsers, 1)} vs previous period` : null
      },
      {
        label: 'New Users',
        value: formatNumberCompact(userData?.newUsers || 0),
        description: 'New signups in this period',
        trend: userData?.growth?.newUsers ? `${formatPercent(userData.growth.newUsers, 1)} vs previous period` : null
      }
    ]
  };
};

/**
 * Generate user section
 */
const generateUserSection = (userData) => {
  return {
    title: '👥 User Metrics',
    metrics: [
      {
        label: 'Verified Users',
        value: formatNumberCompact(userData?.verifiedUsers || 0),
        description: 'Users with verified email',
        percentage: userData?.totalUserCount > 0 
          ? formatPercent((userData.verifiedUsers / userData.totalUserCount) * 100, 1)
          : '0%'
      },
      {
        label: 'Onboarded Users',
        value: formatNumberCompact(userData?.onboardedUsers || 0),
        description: 'Users who completed onboarding',
        percentage: userData?.totalUserCount > 0
          ? formatPercent((userData.onboardedUsers / userData.totalUserCount) * 100, 1)
          : '0%'
      },
      {
        label: 'Users with Wallets',
        value: formatNumberCompact(userData?.usersWithWallets || 0),
        description: 'Users who have created wallets',
        percentage: userData?.totalUserCount > 0
          ? formatPercent((userData.usersWithWallets / userData.totalUserCount) * 100, 1)
          : '0%'
      }
    ]
  };
};

/**
 * Generate financial section
 */
const generateFinancialSection = (txData, financialFlow, splitData, walletData) => {
  return {
    title: '💰 Financial Overview',
    sections: [
      {
        title: 'Transaction Volume',
        metrics: [
          {
            label: 'Total Transaction Value',
            value: formatCurrencyUSD(txData?.totalTransactionValue || 0),
            description: 'Total value of all transactions'
          },
          {
            label: 'Average Transaction',
            value: formatCurrencyUSD(txData?.averageTransactionValue || 0),
            description: 'Average value per transaction'
          }
        ]
      },
      {
        title: 'Splits',
        metrics: [
          {
            label: 'Total Splits',
            value: formatNumberCompact(financialFlow?.splits?.count || splitData?.totalSplits || 0),
            description: 'Total number of splits created'
          },
          {
            label: 'Completed Splits',
            value: formatNumberCompact(financialFlow?.splits?.completed?.count || 0),
            description: 'Splits that have been completed',
            percentage: financialFlow?.splits?.count > 0
              ? formatPercent((financialFlow.splits.completed.count / financialFlow.splits.count) * 100, 1)
              : '0%'
          },
          {
            label: 'Pending Splits',
            value: formatNumberCompact(financialFlow?.splits?.pending?.count || 0),
            description: 'Splits waiting for completion'
          },
          {
            label: 'Total Split Value',
            value: formatCurrencyUSD(financialFlow?.splits?.totalValue || splitData?.totalAmount || 0),
            description: 'Total value of all splits'
          }
        ]
      },
      {
        title: 'Payment Requests',
        metrics: [
          {
            label: 'Total Requests',
            value: formatNumberCompact(financialFlow?.requests?.count || 0),
            description: 'Payment and withdrawal requests'
          },
          {
            label: 'Request Value',
            value: formatCurrencyUSD(financialFlow?.requests?.totalValue || 0),
            description: 'Total value of requests'
          }
        ]
      },
      {
        title: 'Wallets',
        metrics: [
          {
            label: 'Shared Wallets',
            value: formatNumberCompact(financialFlow?.sharedWallets?.count || walletData?.totalWallets || 0),
            description: 'Total shared wallets'
          },
          {
            label: 'Total Wallet Balance',
            value: formatCurrencyUSD(financialFlow?.sharedWallets?.totalBalance || walletData?.totalBalance || 0),
            description: 'Combined balance of all wallets'
          },
          {
            label: 'User Wallet Deposits',
            value: formatCurrencyUSD(financialFlow?.userWallets?.totalDeposits || 0),
            description: 'Total deposits to user wallets'
          }
        ]
      }
    ]
  };
};

/**
 * Generate activity section
 */
const generateActivitySection = (splitData, badgeData, phoneAuthData) => {
  return {
    title: '🎯 User Activity',
    metrics: [
      {
        label: 'Total Splits Created',
        value: formatNumberCompact(splitData?.totalSplits || 0),
        description: 'All splits created in this period'
      },
      {
        label: 'Badge Claims',
        value: formatNumberCompact(badgeData?.totalBadgeClaims || 0),
        description: 'Total badges earned by users'
      },
      {
        label: 'Phone Verified Users',
        value: formatNumberCompact(phoneAuthData?.usersWithVerifiedPhone || 0),
        description: 'Users with verified phone numbers',
        percentage: phoneAuthData?.phoneVerificationRate
          ? formatPercent(phoneAuthData.phoneVerificationRate, 1)
          : '0%'
      }
    ]
  };
};

/**
 * Generate insights section
 */
const generateInsights = (data) => {
  const insights = [];
  const { userData, splitData, txData, financialFlow } = data;

  // User growth insight
  if (userData?.growth?.totalUserCount > 0) {
    insights.push({
      type: 'positive',
      title: 'User Growth',
      message: `User base grew by ${formatPercent(userData.growth.totalUserCount, 1)} compared to the previous period.`
    });
  }

  // Split completion insight
  if (financialFlow?.splits?.completed?.count > 0 && financialFlow?.splits?.count > 0) {
    const completionRate = (financialFlow.splits.completed.count / financialFlow.splits.count) * 100;
    if (completionRate > 70) {
      insights.push({
        type: 'positive',
        title: 'High Split Completion Rate',
        message: `${formatPercent(completionRate, 1)} of splits are being completed, indicating strong user engagement.`
      });
    }
  }

  // Transaction volume insight
  if (txData?.totalTransactionValue > 10000) {
    insights.push({
      type: 'positive',
      title: 'Strong Transaction Volume',
      message: `Total transaction volume reached ${formatCurrencyUSD(txData.totalTransactionValue)}, showing healthy financial activity.`
    });
  }

  // Pending splits insight
  if (financialFlow?.splits?.pending?.count > financialFlow?.splits?.completed?.count) {
    insights.push({
      type: 'warning',
      title: 'Pending Splits',
      message: `There are ${formatNumberCompact(financialFlow.splits.pending.count)} pending splits. Consider following up with users.`
    });
  }

  return {
    title: '💡 Key Insights',
    insights: insights.length > 0 ? insights : [{
      type: 'info',
      title: 'No Insights Available',
      message: 'Insufficient data to generate insights for this period.'
    }]
  };
};

/**
 * Format report as plain text for easy sharing
 */
export const formatReportAsText = (report) => {
  let text = `\n${'='.repeat(60)}\n`;
  text += `  ${report.title}\n`;
  text += `${'='.repeat(60)}\n\n`;
  text += `Generated: ${formatDate(new Date(report.generatedAt))}\n`;
  text += `Period: ${report.period}\n\n`;

  // Overview
  if (report.summary.overview) {
    text += `${report.summary.overview.title}\n`;
    text += `${'-'.repeat(60)}\n`;
    report.summary.overview.metrics.forEach(metric => {
      text += `  ${metric.label}: ${metric.value}`;
      if (metric.trend) text += ` (${metric.trend})`;
      text += `\n    ${metric.description}\n`;
    });
    text += '\n';
  }

  // Users
  if (report.summary.users) {
    text += `${report.summary.users.title}\n`;
    text += `${'-'.repeat(60)}\n`;
    report.summary.users.metrics.forEach(metric => {
      text += `  ${metric.label}: ${metric.value}`;
      if (metric.percentage) text += ` (${metric.percentage})`;
      text += `\n    ${metric.description}\n`;
    });
    text += '\n';
  }

  // Financials
  if (report.summary.financials) {
    text += `${report.summary.financials.title}\n`;
    text += `${'-'.repeat(60)}\n`;
    report.summary.financials.sections.forEach(section => {
      text += `  ${section.title}:\n`;
      section.metrics.forEach(metric => {
        text += `    ${metric.label}: ${metric.value}`;
        if (metric.percentage) text += ` (${metric.percentage})`;
        text += `\n      ${metric.description}\n`;
      });
    });
    text += '\n';
  }

  // Activity
  if (report.summary.activity) {
    text += `${report.summary.activity.title}\n`;
    text += `${'-'.repeat(60)}\n`;
    report.summary.activity.metrics.forEach(metric => {
      text += `  ${metric.label}: ${metric.value}`;
      if (metric.percentage) text += ` (${metric.percentage})`;
      text += `\n    ${metric.description}\n`;
    });
    text += '\n';
  }

  // Insights
  if (report.summary.insights) {
    text += `${report.summary.insights.title}\n`;
    text += `${'-'.repeat(60)}\n`;
    report.summary.insights.insights.forEach(insight => {
      text += `  [${insight.type.toUpperCase()}] ${insight.title}\n`;
      text += `    ${insight.message}\n\n`;
    });
  }

  text += `${'='.repeat(60)}\n`;
  return text;
};

/**
 * Format report as HTML for email sharing
 */
export const formatReportAsHTML = (report) => {
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
        h1 { color: #2c3e50; border-bottom: 3px solid #3498db; padding-bottom: 10px; }
        h2 { color: #34495e; margin-top: 30px; }
        h3 { color: #555; }
        .metric { background: #f8f9fa; padding: 15px; margin: 10px 0; border-left: 4px solid #3498db; border-radius: 4px; }
        .metric-label { font-weight: bold; color: #2c3e50; }
        .metric-value { font-size: 1.5em; color: #3498db; margin: 5px 0; }
        .metric-description { color: #666; font-size: 0.9em; }
        .insight { padding: 15px; margin: 10px 0; border-radius: 4px; }
        .insight.positive { background: #d4edda; border-left: 4px solid #28a745; }
        .insight.warning { background: #fff3cd; border-left: 4px solid #ffc107; }
        .insight.info { background: #d1ecf1; border-left: 4px solid #17a2b8; }
        .section { margin: 20px 0; }
      </style>
    </head>
    <body>
      <h1>${report.title}</h1>
      <p><strong>Generated:</strong> ${formatDate(new Date(report.generatedAt))}</p>
      <p><strong>Period:</strong> ${report.period}</p>
  `;

  // Overview
  if (report.summary.overview) {
    html += `<div class="section"><h2>${report.summary.overview.title}</h2>`;
    report.summary.overview.metrics.forEach(metric => {
      html += `
        <div class="metric">
          <div class="metric-label">${metric.label}</div>
          <div class="metric-value">${metric.value}${metric.trend ? ` <small>(${metric.trend})</small>` : ''}</div>
          <div class="metric-description">${metric.description}</div>
        </div>
      `;
    });
    html += '</div>';
  }

  // Financials
  if (report.summary.financials) {
    html += `<div class="section"><h2>${report.summary.financials.title}</h2>`;
    report.summary.financials.sections.forEach(section => {
      html += `<h3>${section.title}</h3>`;
      section.metrics.forEach(metric => {
        html += `
          <div class="metric">
            <div class="metric-label">${metric.label}</div>
            <div class="metric-value">${metric.value}${metric.percentage ? ` <small>(${metric.percentage})</small>` : ''}</div>
            <div class="metric-description">${metric.description}</div>
          </div>
        `;
      });
    });
    html += '</div>';
  }

  // Insights
  if (report.summary.insights) {
    html += `<div class="section"><h2>${report.summary.insights.title}</h2>`;
    report.summary.insights.insights.forEach(insight => {
      html += `
        <div class="insight ${insight.type}">
          <strong>${insight.title}</strong><br>
          ${insight.message}
        </div>
      `;
    });
    html += '</div>';
  }

  html += `</body></html>`;
  return html;
};

